/**
 * InfoCards
 * Analytics summary, market insights, leaderboard, notification list,
 * line movement, tier comparison, and generic fallback cards.
 */

import type {
  AnalyticsSummaryCardData,
  LeaderboardEntry,
  LeaderboardRankCardData,
  LineMovementCardData,
  NotificationListCardData,
  TierComparisonCardData,
} from '@parlay-picker/shared/chatTypes';
import { type ConditionTone, conditionDisplay } from '@parlay-picker/shared/marketCondition';
import { formatAmericanOdds, MISSING_ODDS } from '@parlay-picker/shared/oddsUtils';
import { formatStatLabel } from '@parlay-picker/shared/statLabels';
import {
  ArrowDown,
  ArrowUp,
  BarChart3,
  Bell,
  Crown,
  TrendingUp,
  Trophy,
  Users,
} from 'lucide-react';
import type { ReactElement } from 'react';
import { formatLabel, StatCell } from './cardHelpers';

// =============================================================================
// Analytics Summary Card
// =============================================================================

export function AnalyticsSummaryCard({ data }: { data: AnalyticsSummaryCardData }): ReactElement {
  // Market insights variant
  if (data.type === 'market_insights') {
    return <MarketInsightsCard data={data} />;
  }

  // User insights
  const insights = data.insights;
  if (!insights) {
    return (
      <div className="bg-theme-surface border border-theme-border rounded-xl p-3">
        <div className="flex items-center gap-2 mb-2">
          <BarChart3 className="w-4 h-4 text-theme-primary" />
          <span className="text-sm font-semibold text-theme-text">Analytics</span>
        </div>
        <div className="text-xs text-theme-text-secondary">No analytics data available yet.</div>
      </div>
    );
  }

  const winRate = insights.winRate != null ? Number(insights.winRate) : null;
  const roi = insights.roi != null ? Number(insights.roi) : null;
  const totalBets = insights.totalBets != null ? Number(insights.totalBets) : null;
  const totalWins = insights.totalWins != null ? Number(insights.totalWins) : null;
  const totalLosses = insights.totalLosses != null ? Number(insights.totalLosses) : null;
  const legHitRate = insights.legHitRate != null ? Number(insights.legHitRate) : null;
  const totalProfit = insights.totalProfit != null ? String(insights.totalProfit) : null;
  const bestPlayers = insights.bestPlayers;
  const worstProps = insights.worstProps;

  return (
    <div className="bg-theme-surface border border-theme-border rounded-xl p-3 space-y-3">
      <div className="flex items-center gap-2">
        <BarChart3 className="w-4 h-4 text-theme-primary" />
        <span className="text-sm font-semibold text-theme-text">Analytics Summary</span>
      </div>

      {/* Key metrics */}
      <div className="grid grid-cols-3 gap-2">
        {winRate != null && <StatCell label="Win Rate" value={`${Number(winRate).toFixed(1)}%`} />}
        {roi != null && (
          <StatCell
            label="ROI"
            value={`${Number(roi) >= 0 ? '+' : ''}${Number(roi).toFixed(1)}%`}
            color={Number(roi) >= 0 ? 'text-theme-success' : 'text-theme-danger'}
          />
        )}
        {totalBets != null && <StatCell label="Bets" value={String(totalBets)} />}
      </div>

      {/* Record + profit */}
      <div className="flex items-center justify-between text-xs text-theme-text-secondary border-t border-theme-border/50 pt-2">
        {totalWins != null && totalLosses != null && (
          <span>
            Record: <span className="text-theme-success">{totalWins}W</span> -{' '}
            <span className="text-theme-danger">{totalLosses}L</span>
          </span>
        )}
        {legHitRate != null && <span>Leg hit rate: {Number(legHitRate).toFixed(1)}%</span>}
        {totalProfit != null && (
          <span className={Number(totalProfit) >= 0 ? 'text-theme-success' : 'text-theme-danger'}>
            {Number(totalProfit) >= 0 ? '+' : ''}${totalProfit}
          </span>
        )}
      </div>

      {/* Best players */}
      {bestPlayers && bestPlayers.length > 0 && (
        <div className="border-t border-theme-border/50 pt-2">
          <div className="text-[10px] text-theme-text-tertiary mb-1">TOP PLAYERS</div>
          <div className="flex flex-wrap gap-1">
            {bestPlayers.slice(0, 4).map((p, i) => (
              <span
                key={i}
                className="text-[10px] px-1.5 py-0.5 rounded bg-theme-success/10 text-theme-success"
              >
                {String(p.player)} {p.hitRate ? `(${p.hitRate}%)` : ''}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Worst props */}
      {worstProps && worstProps.length > 0 && (
        <div>
          <div className="text-[10px] text-theme-text-tertiary mb-1">AVOID PROPS</div>
          <div className="flex flex-wrap gap-1">
            {worstProps.slice(0, 4).map((p, i) => (
              <span
                key={i}
                className="text-[10px] px-1.5 py-0.5 rounded bg-theme-danger/10 text-theme-danger"
              >
                {String(p.prop)} {p.hitRate ? `(${p.hitRate}%)` : ''}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// =============================================================================
// Market Insights Card
// =============================================================================

export function MarketInsightsCard({ data }: { data: AnalyticsSummaryCardData }): ReactElement {
  const days = data.days;
  const toneClass: Record<ConditionTone, string> = {
    positive: 'bg-theme-success/10 text-theme-success',
    negative: 'bg-theme-danger/10 text-theme-danger',
    neutral: 'bg-theme-bg text-theme-text-tertiary',
  };

  return (
    <div className="bg-theme-surface border border-theme-border rounded-xl p-3 space-y-2">
      <div className="flex items-center gap-2">
        <TrendingUp className="w-4 h-4 text-theme-primary" />
        <span className="text-sm font-semibold text-theme-text">Market Insights</span>
      </div>
      {days && days.length > 0 ? (
        <div className="space-y-1.5">
          {days.slice(0, 5).map((day, i) => {
            const stats = day.legStats as Record<string, unknown> | undefined;
            const condition = day.marketCondition ? conditionDisplay(day.marketCondition) : null;
            return (
              <div
                key={i}
                className="flex items-center justify-between text-xs border-t border-theme-border/50 pt-1 first:border-0 first:pt-0"
              >
                <div className="flex items-center gap-1.5">
                  <span className="text-theme-text-secondary">{String(day.date || '')}</span>
                  {condition && (
                    <span
                      className={`text-[10px] px-1 py-0.5 rounded ${toneClass[condition.tone]}`}
                    >
                      {condition.label}
                    </span>
                  )}
                </div>
                {stats && (
                  <span className="text-theme-text-tertiary">
                    {stats.hitRate != null ? `${Number(stats.hitRate).toFixed(0)}%` : ''} (
                    {String(stats.total || '?')} legs)
                  </span>
                )}
              </div>
            );
          })}
        </div>
      ) : (
        <div className="text-xs text-theme-text-secondary">No market data available.</div>
      )}
    </div>
  );
}

// =============================================================================
// Leaderboard Rank Card
// =============================================================================

export function LeaderboardRankCard({ data }: { data: LeaderboardRankCardData }): ReactElement {
  const rank = data.rank;
  const percentile = data.percentile;
  const totalUsers = data.totalUsers;
  const stats = data.stats;
  const top5 = data.top5;

  return (
    <div className="bg-theme-surface border border-theme-border rounded-xl p-3 space-y-2">
      <div className="flex items-center gap-2">
        <Trophy className="w-4 h-4 text-theme-warning" />
        <span className="text-sm font-semibold text-theme-text">Leaderboard</span>
      </div>

      {/* User rank */}
      <div className="flex items-center gap-4">
        {rank != null && (
          <div className="text-center">
            <div className="text-2xl font-bold text-theme-primary">#{rank}</div>
            {totalUsers && (
              <div className="text-[10px] text-theme-text-tertiary">of {totalUsers}</div>
            )}
          </div>
        )}
        {stats && (
          <div className="flex-1 grid grid-cols-2 gap-2">
            {stats.roi != null && (
              <StatCell
                label="ROI"
                value={`${Number(stats.roi) >= 0 ? '+' : ''}${Number(stats.roi).toFixed(1)}%`}
                color={Number(stats.roi) >= 0 ? 'text-theme-success' : 'text-theme-danger'}
              />
            )}
            {stats.winRate != null && (
              <StatCell label="Win Rate" value={`${Number(stats.winRate).toFixed(1)}%`} />
            )}
            {stats.wins != null && stats.losses != null && (
              <StatCell label="Record" value={`${stats.wins}-${stats.losses}`} />
            )}
            {percentile != null && (
              <StatCell label="Percentile" value={`Top ${percentile.toFixed(0)}%`} />
            )}
          </div>
        )}
      </div>

      {/* Top 5 */}
      {top5 && top5.length > 0 && (
        <div className="border-t border-theme-border/50 pt-2 space-y-1">
          <div className="text-[10px] text-theme-text-tertiary">TOP 5</div>
          {top5.map((entry: LeaderboardEntry, i) => {
            const user = entry.user;
            const entryStats = entry.stats;
            const displayName = user ? user.displayName || 'Anonymous' : `#${i + 1}`;
            return (
              <div key={i} className="flex items-center justify-between text-xs">
                <span className="text-theme-text">
                  <span className="text-theme-text-tertiary">#{String(entry.rank || i + 1)}</span>{' '}
                  {displayName}
                </span>
                {entryStats && (
                  <span
                    className={entryStats.roi >= 0 ? 'text-theme-success' : 'text-theme-danger'}
                  >
                    {entryStats.roi >= 0 ? '+' : ''}
                    {entryStats.roi.toFixed(1)}% ROI
                  </span>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// =============================================================================
// Notification List Card
// =============================================================================

export function NotificationListCard({ data }: { data: NotificationListCardData }): ReactElement {
  const notifications = data.notifications;
  const unreadCount = data.unreadCount || 0;

  return (
    <div className="bg-theme-surface border border-theme-border rounded-xl p-3 space-y-2">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Bell className="w-4 h-4 text-theme-primary" />
          <span className="text-sm font-semibold text-theme-text">Notifications</span>
        </div>
        {unreadCount > 0 && (
          <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-theme-danger text-white font-medium">
            {unreadCount} unread
          </span>
        )}
      </div>
      {notifications && notifications.length > 0 ? (
        <div className="space-y-1.5">
          {notifications.slice(0, 8).map((n, i) => {
            const isRead = !!n.readAt;
            return (
              <div
                key={i}
                className={`flex items-start gap-2 text-xs ${isRead ? 'opacity-60' : ''}`}
              >
                <div
                  className={`w-1.5 h-1.5 rounded-full mt-1.5 shrink-0 ${isRead ? 'bg-theme-text-tertiary' : 'bg-theme-primary'}`}
                />
                <div className="min-w-0">
                  <div className="text-theme-text font-medium truncate">
                    {String(n.title || n.type || 'Notification')}
                  </div>
                  {n.body != null && (
                    <div className="text-theme-text-secondary truncate">
                      {String(n.body || n.message || '')}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="text-xs text-theme-text-secondary">No notifications</div>
      )}
    </div>
  );
}

// =============================================================================
// Line Movement / Odds Comparison Card
// =============================================================================

export function LineMovementCard({ data }: { data: LineMovementCardData }): ReactElement {
  const playerName = data.playerName || 'Unknown';
  const statType = data.statType || '';
  const books = data.books;

  return (
    <div className="bg-theme-surface border border-theme-border rounded-xl p-3 space-y-2">
      <div className="flex items-center gap-2">
        <TrendingUp className="w-4 h-4 text-theme-primary" />
        <span className="text-sm font-semibold text-theme-text">{playerName}</span>
        <span className="text-xs text-theme-text-secondary">{formatStatLabel(statType)}</span>
      </div>
      {books && books.length > 0 ? (
        <div className="space-y-1">
          {books.map((book, i) => (
            <div key={i} className="flex items-center justify-between text-xs">
              <span className="text-theme-text capitalize">
                {String(book.bookmaker || book.name || 'Unknown')}
              </span>
              <div className="flex items-center gap-3">
                <span className="text-theme-text-secondary">
                  {book.line != null ? String(book.line) : '?'}
                </span>
                <div className="flex items-center gap-1.5">
                  {book.overPrice != null && (
                    <span className="flex items-center gap-0.5">
                      <ArrowUp className="w-3 h-3 text-theme-success" />
                      <span className="text-theme-text-secondary">
                        {formatAmericanOdds(book.overPrice) ?? MISSING_ODDS}
                      </span>
                    </span>
                  )}
                  {book.underPrice != null && (
                    <span className="flex items-center gap-0.5">
                      <ArrowDown className="w-3 h-3 text-theme-danger" />
                      <span className="text-theme-text-secondary">
                        {formatAmericanOdds(book.underPrice) ?? MISSING_ODDS}
                      </span>
                    </span>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="text-xs text-theme-text-secondary">No odds data available</div>
      )}
    </div>
  );
}

// =============================================================================
// Tier Comparison Card
// =============================================================================

export function TierComparisonCard({ data }: { data: TierComparisonCardData }): ReactElement {
  const tiers = data.tiers;
  if (!tiers)
    return <GenericCard data={data as unknown as Record<string, unknown>} type="tier_comparison" />;

  const tierColors: Record<string, string> = {
    Free: 'text-theme-text-secondary',
    Pro: 'text-theme-primary',
    Premium: 'text-theme-warning',
    Elite: 'text-[#a855f7]',
  };

  return (
    <div className="bg-theme-surface border border-theme-border rounded-xl p-3 space-y-3">
      <div className="flex items-center gap-2">
        <Crown className="w-4 h-4 text-theme-warning" />
        <span className="text-sm font-semibold text-theme-text">Tier Comparison</span>
      </div>
      <div className="space-y-3">
        {tiers.map((tier) => (
          <div
            key={tier.name}
            className="border-b border-theme-border last:border-0 pb-2 last:pb-0"
          >
            <div className="flex items-center justify-between mb-1">
              <span
                className={`text-sm font-semibold ${tierColors[tier.name] || 'text-theme-text'}`}
              >
                {tier.name}
              </span>
              <span className="text-xs text-theme-text-secondary">{tier.price}</span>
            </div>
            <div className="flex flex-wrap gap-1">
              {tier.features.map((f, i) => (
                <span
                  key={i}
                  className="text-[10px] px-1.5 py-0.5 rounded bg-theme-bg text-theme-text-secondary"
                >
                  {f}
                </span>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// =============================================================================
// Generic Fallback Card
// =============================================================================

export function GenericCard({
  data,
  type,
}: {
  data: Record<string, unknown>;
  type: string;
}): ReactElement {
  // Try to render key-value pairs instead of raw JSON
  const entries = Object.entries(data).filter(([, v]) => v != null && typeof v !== 'object');

  return (
    <div className="bg-theme-surface border border-theme-border rounded-xl p-3">
      <div className="flex items-center gap-2 mb-2">
        <Users className="w-4 h-4 text-theme-text-secondary" />
        <span className="text-sm font-semibold text-theme-text">{formatLabel(type)}</span>
      </div>
      {entries.length > 0 ? (
        <div className="grid grid-cols-2 gap-1">
          {entries.slice(0, 8).map(([key, value]) => (
            <div key={key} className="text-xs">
              <span className="text-theme-text-tertiary">{formatLabel(key)}: </span>
              <span className="text-theme-text">{String(value)}</span>
            </div>
          ))}
        </div>
      ) : (
        <div className="text-xs text-theme-text-secondary">Data loaded — see details above.</div>
      )}
    </div>
  );
}
